import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    useDisclosure,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    VStack,
    HStack,
    IconButton,
    Text,
    Checkbox,
    CheckboxGroup,
    Stack,
    NumberInput,
    NumberInputField,
    Heading,
    Code,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Tooltip,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, RepeatIcon, CopyIcon } from '@chakra-ui/icons';

const API_BASE = '/api/v1/admin-ui';

function ApiKeys() {
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedKey, setSelectedKey] = useState(null);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
    const toast = useToast();
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isKeyDisplayOpen, onOpen: onKeyDisplayOpen, onClose: onKeyDisplayClose } = useDisclosure();

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        permissions: {
            can_create_users: false,
            can_read_users: false,
            can_update_users: false,
            can_delete_users: false,
            can_manage_tags: false,
        },
        rate_limit_per_minute: 60,
    });

    const fetchApiKeys = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const instanceId = localStorage.getItem('instance_id');
            const response = await fetch(`${API_BASE}/api-keys/`, {
                headers: {
                    'Authorization': `Token ${token}`,
                    'X-Tenant-ID': instanceId,
                },
            });
            const data = await response.json();
            if (data.success) {
                setApiKeys(data.api_keys);
            }
        } catch (error) {
            toast({
                title: 'Error fetching API keys',
                description: error.message,
                status: 'error',
                duration: 3000,
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchApiKeys();
    }, [fetchApiKeys]);

    const handleCreate = async () => {
        try {
            const token = localStorage.getItem('token');
            const instanceId = localStorage.getItem('instance_id');
            const response = await fetch(`${API_BASE}/api-keys/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${token}`,
                    'X-Tenant-ID': instanceId,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (data.success) {
                setNewlyCreatedKey(data.api_key);
                onCreateClose();
                onKeyDisplayOpen();
                fetchApiKeys();
                resetForm();
            } else {
                toast({
                    title: 'Error',
                    description: data.message,
                    status: 'error',
                    duration: 3000,
                });
            }
        } catch (error) {
            toast({
                title: 'Error creating API key',
                description: error.message,
                status: 'error',
                duration: 3000,
            });
        }
    };

    const handleUpdate = async () => {
        try {
            const token = localStorage.getItem('token');
            const instanceId = localStorage.getItem('instance_id');
            const response = await fetch(`${API_BASE}/api-keys/${selectedKey.id}/`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Token ${token}`,
                    'X-Tenant-ID': instanceId,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (data.success) {
                toast({
                    title: 'API key updated',
                    status: 'success',
                    duration: 2000,
                });
                onEditClose();
                fetchApiKeys();
            } else {
                toast({
                    title: 'Error',
                    description: data.message,
                    status: 'error',
                    duration: 3000,
                });
            }
        } catch (error) {
            toast({
                title: 'Error updating API key',
                description: error.message,
                status: 'error',
                duration: 3000,
            });
        }
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            const instanceId = localStorage.getItem('instance_id');
            const response = await fetch(`${API_BASE}/api-keys/${selectedKey.id}/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Token ${token}`,
                    'X-Tenant-ID': instanceId,
                },
            });
            const data = await response.json();
            if (data.success) {
                toast({
                    title: 'API key deleted',
                    status: 'success',
                    duration: 2000,
                });
                onDeleteClose();
                fetchApiKeys();
            }
        } catch (error) {
            toast({
                title: 'Error deleting API key',
                description: error.message,
                status: 'error',
                duration: 3000,
            });
        }
    };

    const handleRegenerate = async (key) => {
        try {
            const token = localStorage.getItem('token');
            const instanceId = localStorage.getItem('instance_id');
            const response = await fetch(`${API_BASE}/api-keys/${key.id}/regenerate/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${token}`,
                    'X-Tenant-ID': instanceId,
                },
            });
            const data = await response.json();
            if (data.success) {
                setNewlyCreatedKey(data.api_key);
                onKeyDisplayOpen();
                fetchApiKeys();
            }
        } catch (error) {
            toast({
                title: 'Error regenerating API key',
                description: error.message,
                status: 'error',
                duration: 3000,
            });
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied to clipboard',
            status: 'success',
            duration: 2000,
        });
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            permissions: {
                can_create_users: false,
                can_read_users: false,
                can_update_users: false,
                can_delete_users: false,
                can_manage_tags: false,
            },
            rate_limit_per_minute: 60,
        });
    };

    const openEditModal = (key) => {
        setSelectedKey(key);
        setFormData({
            name: key.name,
            description: key.description || '',
            permissions: key.permissions,
            rate_limit_per_minute: key.rate_limit_per_minute,
        });
        onEditOpen();
    };

    const openDeleteModal = (key) => {
        setSelectedKey(key);
        onDeleteOpen();
    };

    const handlePermissionChange = (permission, checked) => {
        setFormData({
            ...formData,
            permissions: {
                ...formData.permissions,
                [permission]: checked,
            },
        });
    };

    if (loading) {
        return <Box p={6}>Loading...</Box>;
    }

    return (
        <Box>
            <HStack justify="space-between" mb={6}>
                <Heading size="lg">API Keys</Heading>
                <Button 
                    leftIcon={<AddIcon />} 
                    colorScheme="orange" 
                    onClick={() => { resetForm(); onCreateOpen(); }}
                >
                    Create API Key
                </Button>
            </HStack>

            <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
                <Table variant="simple">
                    <Thead bg="gray.50">
                        <Tr>
                            <Th>Name</Th>
                            <Th>Key Preview</Th>
                            <Th>Permissions</Th>
                            <Th>Status</Th>
                            <Th>Usage</Th>
                            <Th>Last Used</Th>
                            <Th>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {apiKeys.map((key) => (
                            <Tr key={key.id}>
                                <Td>
                                    <Text fontWeight="medium">{key.name}</Text>
                                    {key.description && (
                                        <Text fontSize="sm" color="gray.500">{key.description}</Text>
                                    )}
                                </Td>
                                <Td>
                                    <Code fontSize="sm">{key.key_preview}</Code>
                                </Td>
                                <Td>
                                    <HStack spacing={1} flexWrap="wrap">
                                        {key.permissions.can_create_users && (
                                            <Badge colorScheme="green" size="sm">Create</Badge>
                                        )}
                                        {key.permissions.can_read_users && (
                                            <Badge colorScheme="blue" size="sm">Read</Badge>
                                        )}
                                        {key.permissions.can_update_users && (
                                            <Badge colorScheme="yellow" size="sm">Update</Badge>
                                        )}
                                        {key.permissions.can_delete_users && (
                                            <Badge colorScheme="red" size="sm">Delete</Badge>
                                        )}
                                        {key.permissions.can_manage_tags && (
                                            <Badge colorScheme="purple" size="sm">Tags</Badge>
                                        )}
                                    </HStack>
                                </Td>
                                <Td>
                                    <Badge colorScheme={key.active ? 'green' : 'gray'}>
                                        {key.active ? 'Active' : 'Inactive'}
                                    </Badge>
                                </Td>
                                <Td>{key.request_count.toLocaleString()} requests</Td>
                                <Td>
                                    {key.last_used_at 
                                        ? new Date(key.last_used_at).toLocaleDateString() 
                                        : 'Never'}
                                </Td>
                                <Td>
                                    <HStack spacing={2}>
                                        <Tooltip label="Edit">
                                            <IconButton
                                                icon={<EditIcon />}
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => openEditModal(key)}
                                                aria-label="Edit"
                                            />
                                        </Tooltip>
                                        <Tooltip label="Regenerate Key">
                                            <IconButton
                                                icon={<RepeatIcon />}
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="orange"
                                                onClick={() => handleRegenerate(key)}
                                                aria-label="Regenerate"
                                            />
                                        </Tooltip>
                                        <Tooltip label="Delete">
                                            <IconButton
                                                icon={<DeleteIcon />}
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="red"
                                                onClick={() => openDeleteModal(key)}
                                                aria-label="Delete"
                                            />
                                        </Tooltip>
                                    </HStack>
                                </Td>
                            </Tr>
                        ))}
                        {apiKeys.length === 0 && (
                            <Tr>
                                <Td colSpan={7} textAlign="center" py={8}>
                                    <Text color="gray.500">No API keys yet. Create one to get started.</Text>
                                </Td>
                            </Tr>
                        )}
                    </Tbody>
                </Table>
            </Box>

            {/* Create Modal */}
            <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Create API Key</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Name</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g., Integration Service"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="What is this API key used for?"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Permissions</FormLabel>
                                <Stack spacing={2}>
                                    <Checkbox
                                        isChecked={formData.permissions.can_create_users}
                                        onChange={(e) => handlePermissionChange('can_create_users', e.target.checked)}
                                    >
                                        Create Users
                                    </Checkbox>
                                    <Checkbox
                                        isChecked={formData.permissions.can_read_users}
                                        onChange={(e) => handlePermissionChange('can_read_users', e.target.checked)}
                                    >
                                        Read Users
                                    </Checkbox>
                                    <Checkbox
                                        isChecked={formData.permissions.can_update_users}
                                        onChange={(e) => handlePermissionChange('can_update_users', e.target.checked)}
                                    >
                                        Update Users
                                    </Checkbox>
                                    <Checkbox
                                        isChecked={formData.permissions.can_delete_users}
                                        onChange={(e) => handlePermissionChange('can_delete_users', e.target.checked)}
                                    >
                                        Delete Users
                                    </Checkbox>
                                    <Checkbox
                                        isChecked={formData.permissions.can_manage_tags}
                                        onChange={(e) => handlePermissionChange('can_manage_tags', e.target.checked)}
                                    >
                                        Manage Tags
                                    </Checkbox>
                                </Stack>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Rate Limit (requests per minute)</FormLabel>
                                <NumberInput
                                    value={formData.rate_limit_per_minute}
                                    onChange={(_, value) => setFormData({ ...formData, rate_limit_per_minute: value })}
                                    min={1}
                                    max={1000}
                                >
                                    <NumberInputField />
                                </NumberInput>
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onCreateClose}>Cancel</Button>
                        <Button colorScheme="orange" onClick={handleCreate}>Create</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Edit Modal */}
            <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Edit API Key</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Name</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Status</FormLabel>
                                <Checkbox
                                    isChecked={formData.active !== false}
                                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                >
                                    Active
                                </Checkbox>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Permissions</FormLabel>
                                <Stack spacing={2}>
                                    <Checkbox
                                        isChecked={formData.permissions.can_create_users}
                                        onChange={(e) => handlePermissionChange('can_create_users', e.target.checked)}
                                    >
                                        Create Users
                                    </Checkbox>
                                    <Checkbox
                                        isChecked={formData.permissions.can_read_users}
                                        onChange={(e) => handlePermissionChange('can_read_users', e.target.checked)}
                                    >
                                        Read Users
                                    </Checkbox>
                                    <Checkbox
                                        isChecked={formData.permissions.can_update_users}
                                        onChange={(e) => handlePermissionChange('can_update_users', e.target.checked)}
                                    >
                                        Update Users
                                    </Checkbox>
                                    <Checkbox
                                        isChecked={formData.permissions.can_delete_users}
                                        onChange={(e) => handlePermissionChange('can_delete_users', e.target.checked)}
                                    >
                                        Delete Users
                                    </Checkbox>
                                    <Checkbox
                                        isChecked={formData.permissions.can_manage_tags}
                                        onChange={(e) => handlePermissionChange('can_manage_tags', e.target.checked)}
                                    >
                                        Manage Tags
                                    </Checkbox>
                                </Stack>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Rate Limit (requests per minute)</FormLabel>
                                <NumberInput
                                    value={formData.rate_limit_per_minute}
                                    onChange={(_, value) => setFormData({ ...formData, rate_limit_per_minute: value })}
                                    min={1}
                                    max={1000}
                                >
                                    <NumberInputField />
                                </NumberInput>
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onEditClose}>Cancel</Button>
                        <Button colorScheme="orange" onClick={handleUpdate}>Save Changes</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Delete API Key</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>
                            Are you sure you want to delete the API key "{selectedKey?.name}"? 
                            This action cannot be undone and will break any integrations using this key.
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
                        <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Key Display Modal */}
            <Modal isOpen={isKeyDisplayOpen} onClose={onKeyDisplayClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Your API Key</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Alert status="warning" mb={4}>
                            <AlertIcon />
                            <Box>
                                <AlertTitle>Save this key now!</AlertTitle>
                                <AlertDescription>
                                    This is the only time you'll see the full API key. 
                                    Store it securely - you won't be able to see it again.
                                </AlertDescription>
                            </Box>
                        </Alert>
                        <FormControl>
                            <FormLabel>API Key for "{newlyCreatedKey?.name}"</FormLabel>
                            <HStack>
                                <Code 
                                    p={3} 
                                    borderRadius="md" 
                                    bg="gray.100" 
                                    flex={1}
                                    fontSize="sm"
                                    wordBreak="break-all"
                                >
                                    {newlyCreatedKey?.key}
                                </Code>
                                <IconButton
                                    icon={<CopyIcon />}
                                    onClick={() => copyToClipboard(newlyCreatedKey?.key)}
                                    aria-label="Copy"
                                    colorScheme="orange"
                                />
                            </HStack>
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="orange" onClick={onKeyDisplayClose}>
                            I've Saved the Key
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}

export default ApiKeys;
